import Phaser from 'phaser';
import type { FactionId } from '../../shared/types';
import { FACTIONS, FACTION_COLORS } from '../../shared/factions';

/**
 * W8 PR-4 — Hub sprite atlas loader infra.
 *
 * 5 門派建築物 spritesheet 路徑常數。Boss 提供 asset 後填入真實路徑;
 * 在 asset 未到位時,Phaser loader 會靜默 404 → `textures.exists(key)` 回傳
 * false → constructor fallback 回 W7 Rectangle placeholder,不阻塞 PR merge。
 *
 * Test 約定 — 這個 const 必須涵蓋全部 5 FactionId(test #1 driver)。
 */
export const HUB_BUILDING_ATLAS_KEY = 'hub-building-atlas';

export const HUB_BUILDING_ATLAS: Record<FactionId, { key: string; url: string }> = {
  celestial: { key: 'hub-building-celestial', url: 'assets/hub/celestial-tower.png' },
  runic:     { key: 'hub-building-runic',     url: 'assets/hub/runic-shrine.png' },
  techno:    { key: 'hub-building-techno',    url: 'assets/hub/techno-workshop.png' },
  elemental: { key: 'hub-building-elemental', url: 'assets/hub/elemental-forest.png' },
  dominion:  { key: 'hub-building-dominion',  url: 'assets/hub/dominion-wall.png' },
};

/** spec 對齊用 — 各 faction 對應的真圖 texture key,快查表。 */
export const HUB_BUILDING_TEXTURE: Record<FactionId, string> = {
  celestial: HUB_BUILDING_ATLAS.celestial.key,
  runic:     HUB_BUILDING_ATLAS.runic.key,
  techno:    HUB_BUILDING_ATLAS.techno.key,
  elemental: HUB_BUILDING_ATLAS.elemental.key,
  dominion:  HUB_BUILDING_ATLAS.dominion.key,
};

/**
 * W7 — HubBuilding entity.
 *
 * 5 門派建築物 (placeholder Rectangle + emoji + displayName) — W7 不放真實 sprite;
 * W8 PR-4 提供 atlas loader infra,真圖到位後 constructor 自動 swap 為 sprite。
 *
 * 構造:
 *   container (x, y, depth 0)
 *     ├ sprite      (faction 真圖,若 texture 存在 — W8 PR-4)
 *     ├ bgRect      (100×120, faction.colorPrimary) — 真圖未到時 fallback (W7)
 *     ├ emojiText   (24px, faction.emoji)
 *     ├ nameText    (16px, faction.displayName)
 *     ├ lockOverlay (alpha 0.45, 顯示僅當 locked=true)
 *     ├ lockIcon    ('🔒', 顯示僅當 locked=true)
 *     └ conqueredIcon ('✓', initial hidden, by showConquered())
 *
 * 全部方法 try-guard,於 headless test harness 不丟例外
 * (對齊 JuiceLayer / ParallaxSystem 既有風格)。
 */
export class HubBuilding {
  /**
   * W8 PR-4 — HubScene.preload() 內呼叫,嘗試 load 5 faction atlas image。
   *
   * 真圖未到位時 url 為 404,Phaser loader 會靜默 fail → constructor
   * `textures.exists(key)` 回傳 false → fallback W7 Rectangle。本 method
   * 不 throw、silent skip,headless test harness 也安全。
   */
  static preloadAtlas(scene: Phaser.Scene): void {
    try {
      const tx = (scene as { textures?: { exists?: (k: string) => boolean } }).textures;
      const loader = (scene as { load?: { image?: (k: string, u: string) => void } }).load;
      if (!loader || typeof loader.image !== 'function') {
        return; // headless / no loader plugin
      }
      for (const entry of Object.values(HUB_BUILDING_ATLAS)) {
        try {
          if (tx?.exists?.(entry.key)) continue; // already loaded
          loader.image(entry.key, entry.url);
        } catch { /* per-entry guard */ }
      }
    } catch { /* headless */ }
  }

  /** 卡片視覺尺寸(較舊 placeholder 100×120 略大,留白更舒服)。 */
  static readonly CARD_W = 116;
  static readonly CARD_H = 140;

  readonly factionId: FactionId;
  readonly locked: boolean;
  readonly container: Phaser.GameObjects.Container;

  /** Bg rectangle — public so test 可斷言 alpha 0.45 lock overlay。 */
  readonly bgRect: Phaser.GameObjects.Rectangle | null = null;
  /** W8 PR-4 — 若 atlas texture 存在,改用真圖;否則 null,bgRect 接手。 */
  readonly sprite: Phaser.GameObjects.Image | null = null;
  readonly lockOverlay: Phaser.GameObjects.Rectangle | null = null;
  readonly lockIcon: Phaser.GameObjects.Text | null = null;
  readonly conqueredIcon: Phaser.GameObjects.Text | null = null;
  /** 門派色光暈塊 — hover 時放亮(視覺升級用)。 */
  readonly glow: Phaser.GameObjects.Rectangle | null = null;
  /** 卡片底板 — 描邊/面板,hover 時可加亮描邊。 */
  readonly card: Phaser.GameObjects.Rectangle | null = null;

  /** 對應 scene 參考,shake / hover / showConquered 用。 */
  private readonly scene: Phaser.Scene;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    factionId: FactionId,
    locked: boolean,
  ) {
    this.scene = scene;
    this.factionId = factionId;
    this.locked = locked;

    const faction = FACTIONS[factionId];
    const colors = FACTION_COLORS[factionId];
    const primary = faction.colorPrimary;
    const accent = colors.accent;

    // ── 卡片尺寸常數 ──────────────────────────────────────────────────────────
    // 視覺升級(boss 2026-06-13「選哪一個門那邊太醜」):每門派做成一張有
    // 門派色光暈 + 厚描邊的「門卡」。藝術圖框在上半,單一名牌在下半,不再
    // 讓 emoji/門派名疊在建築圖上。卡片本體比舊 placeholder 略大,留白更舒服。
    const CARD_W = HubBuilding.CARD_W;
    const CARD_H = HubBuilding.CARD_H;
    // 藝術圖框:卡片內部上方一塊正方,讓側視建築圖完整顯示不被裁切。
    const ART_W = CARD_W - 22;
    const ART_H = 78;
    const ART_CY = -CARD_H / 2 + 14 + ART_H / 2; // 圖框中心 y(卡片頂內縮)

    // 建 container — placeholder 為 (0,0,[]) ,稍後 add()
    let container: Phaser.GameObjects.Container;
    try {
      container = scene.add.container(x, y);
    } catch {
      // headless: 提供 minimal stub 讓 readonly fields 不為 undefined。
      container = {
        x, y,
        scaleX: 1, scaleY: 1,
        add: () => container,
        setSize: () => container,
        setInteractive: () => container,
        on: () => container,
        setDepth: () => container,
        list: [],
      } as unknown as Phaser.GameObjects.Container;
    }
    this.container = container;

    // ── 門派色光暈(卡片後方放大的半透明同色塊,模擬發光)。hover 時放亮。──
    // 用 rectangle(全環境 stub 支援),避免 circle/graphics 在精簡 test stub 缺席。
    try {
      const glow = scene.add.rectangle(0, 4, CARD_W + 26, CARD_H + 26, primary, 0.22);
      try { (glow as unknown as { setDepth?: (d: number) => void }).setDepth?.(-1); } catch { /* noop */ }
      this.container.add(glow);
      (this as { glow: Phaser.GameObjects.Rectangle }).glow = glow;
    } catch { /* headless */ }

    // ── W8 PR-4: 嘗試 atlas sprite;若 texture 不存在 → W7 Rectangle fallback ──
    // 重要:`assets/hub/*.png` 本身就是「整張門派卡」美術(已含外框 + 門派名),
    // 所以有真圖時讓圖「鋪滿卡片」當主體,**不再**疊我自己的展示窗/名牌(會雙重
    // 外框 + 雙重門派名)。只在無真圖的 placeholder 路徑才畫展示窗 + emoji + 名牌。
    const atlasKey = HUB_BUILDING_TEXTURE[factionId];
    const hasTexture = (() => {
      try {
        const tx = (scene as { textures?: { exists?: (k: string) => boolean } }).textures;
        return atlasKey ? !!tx?.exists?.(atlasKey) : false;
      } catch { return false; }
    })();

    // 卡片底板(深色面板 + 門派色厚描邊)。有真圖時當「圖的襯底/外框」,
    // placeholder 時當卡片本體。兩路都需要,所以先畫。
    try {
      const card = scene.add.rectangle(0, 0, CARD_W, CARD_H, 0x121826, 0.96);
      try {
        (card as unknown as { setStrokeStyle?: (w: number, c: number, a?: number) => void })
          .setStrokeStyle?.(4, accent, 0.95);
      } catch { /* noop */ }
      this.container.add(card);
      (this as { card: Phaser.GameObjects.Rectangle }).card = card;
    } catch { /* headless */ }

    if (hasTexture) {
      // 真圖 = 完整門派卡:鋪滿卡片內緣(留 6px 邊讓底板描邊露出當外框)。
      try {
        const img = scene.add.image(0, 0, atlasKey);
        try {
          (img as unknown as { setDisplaySize?: (w: number, h: number) => void })
            .setDisplaySize?.(CARD_W - 8, CARD_H - 8);
        } catch { /* headless */ }
        this.container.add(img);
        (this as { sprite: Phaser.GameObjects.Image }).sprite = img;
      } catch { /* headless */ }
    } else {
      // ── Placeholder 路徑:展示窗 + primary 色塊 + emoji + 名牌(無真圖時的設計)──
      // 展示窗底(門派色暗塊)。
      try {
        const frame = scene.add.rectangle(0, ART_CY, ART_W, ART_H, primary, 0.34);
        try {
          (frame as unknown as { setStrokeStyle?: (w: number, c: number, a?: number) => void })
            .setStrokeStyle?.(2, accent, 0.5);
        } catch { /* noop */ }
        this.container.add(frame);
      } catch { /* headless */ }
      // primary 色塊(W7 既有 bgRect 行為,測試斷言它存在且進 container)。
      try {
        const bg = scene.add.rectangle(0, ART_CY, ART_W - 8, ART_H - 8, faction.colorPrimary);
        this.container.add(bg);
        (this as { bgRect: Phaser.GameObjects.Rectangle }).bgRect = bg;
      } catch { /* headless */ }
      // 門派 emoji(展示窗中央)。
      try {
        const emojiText = scene.add.text(0, ART_CY, faction.emoji, {
          fontSize: '34px', fontFamily: 'sans-serif',
        });
        try { emojiText.setOrigin(0.5); } catch { /* headless */ }
        this.container.add(emojiText);
      } catch { /* headless */ }
      // 名牌(卡片下半,門派色底條 + 白字)。
      try {
        const plateY = CARD_H / 2 - 22;
        const plate = scene.add.rectangle(0, plateY, CARD_W - 16, 30, primary, 0.9);
        try {
          (plate as unknown as { setStrokeStyle?: (w: number, c: number, a?: number) => void })
            .setStrokeStyle?.(2, accent, 0.6);
        } catch { /* noop */ }
        this.container.add(plate);
        const nameText = scene.add.text(0, plateY, faction.displayName, {
          fontSize: '17px', fontFamily: 'sans-serif', color: '#ffffff', fontStyle: 'bold',
        });
        try { nameText.setOrigin(0.5); } catch { /* headless */ }
        this.container.add(nameText);
      } catch { /* headless */ }
    }

    // ── Lock overlay (semi-transparent + 🔒 icon) — 僅 locked=true 時可見 ──
    // ⚠️ 維持「恰好一個 alpha 0.45 rect + 一個 🔒 text」的測試約定。
    if (locked) {
      try {
        const overlay = scene.add.rectangle(0, 0, CARD_W, CARD_H, 0x000000, 0.45);
        this.container.add(overlay);
        (this as { lockOverlay: Phaser.GameObjects.Rectangle }).lockOverlay = overlay;
      } catch { /* headless */ }

      try {
        const lockIcon = scene.add.text(0, 0, '🔒', {
          fontSize: '34px', fontFamily: 'sans-serif',
        });
        try { lockIcon.setOrigin(0.5); } catch { /* headless */ }
        this.container.add(lockIcon);
        (this as { lockIcon: Phaser.GameObjects.Text }).lockIcon = lockIcon;
      } catch { /* headless */ }
    }

    // ── Conquered icon (✓, initial hidden) — 右上角徽記。────────────────────────
    try {
      const conqIcon = scene.add.text(CARD_W / 2 - 12, -CARD_H / 2 + 6, '✓', {
        fontSize: '24px', fontFamily: 'sans-serif', color: '#ffffff', fontStyle: 'bold',
        backgroundColor: '#2e7d32', padding: { x: 5, y: 1 },
      });
      try { conqIcon.setOrigin(0.5); } catch { /* headless */ }
      try { (conqIcon as unknown as { setVisible?: (v: boolean) => void }).setVisible?.(false); } catch { /* noop */ }
      this.container.add(conqIcon);
      (this as { conqueredIcon: Phaser.GameObjects.Text }).conqueredIcon = conqIcon;
    } catch { /* headless */ }

    // ── Interactive area + hover wiring ──────────────────────────────────────
    try {
      this.container.setSize(CARD_W, CARD_H);
      this.container.setInteractive(
        new Phaser.Geom.Rectangle(-CARD_W / 2, -CARD_H / 2, CARD_W, CARD_H),
        Phaser.Geom.Rectangle.Contains,
      );
      this.container.on('pointerover', () => this.onHover());
      this.container.on('pointerout',  () => this.onHoverOut());
    } catch { /* headless: setSize / setInteractive 未實作 */ }
  }

  /** HubScene.onBuildingClick 在 locked 時呼叫,執行小幅度水平 shake。 */
  shake(): void {
    try {
      const originalX = this.container.x;
      this.scene.tweens.add({
        targets: this.container,
        x: originalX + 8,
        duration: 50,
        yoyo: true,
        repeat: 2,
        ease: 'Sine.InOut',
        onComplete: () => { try { this.container.x = originalX; } catch { /* noop */ } },
      });
    } catch { /* headless / no tweens */ }
  }

  /** boss 撃破後 ✓ 顯示 + scale punch tween。 */
  showConquered(): void {
    try {
      const icon = this.conqueredIcon as Phaser.GameObjects.Text | null;
      if (icon) {
        try { (icon as unknown as { setVisible?: (v: boolean) => void }).setVisible?.(true); } catch { /* noop */ }
      }
    } catch { /* noop */ }
    try {
      const setScale = (this.container as unknown as { setScale?: (s: number) => void }).setScale;
      setScale?.call(this.container, 1.3);
      this.scene.tweens.add({
        targets: this.container,
        scaleX: 1,
        scaleY: 1,
        duration: 300,
        ease: 'Elastic.Out',
        easeParams: [1.2, 0.5],
      });
    } catch { /* headless */ }
  }

  /**
   * pointerover handler — scale 1.05 with Back.Out ease.
   *
   * Polish 對齊 W3 JuiceLayer convention(scalePunch 用 Elastic.Out;hover 用
   * Back.Out 較柔和不過彈)+ W7 spec §6.1 規範。Duration 150ms (W7 spec §6.1)
   * 較 W7 ship 的 120ms 稍長,讓 hover 有「軟落地」反饋,不會太尖銳。
   */
  onHover(): void {
    try {
      this.scene.tweens.add({
        targets: this.container,
        scaleX: 1.05,
        scaleY: 1.05,
        duration: 150,
        ease: 'Back.Out',
      });
    } catch { /* headless */ }
    // 門派色光暈放亮 — 讓「滑到/選中哪個門」有清楚的發光回饋。
    try {
      if (this.glow) {
        this.scene.tweens.add({
          targets: this.glow, alpha: 0.5, scaleX: 1.12, scaleY: 1.12,
          duration: 150, ease: 'Sine.Out',
        });
      }
    } catch { /* headless */ }
  }

  /**
   * pointerout handler — 復歸 scale 1.0,duration 對齊 onHover 150ms。
   */
  onHoverOut(): void {
    try {
      this.scene.tweens.add({
        targets: this.container,
        scaleX: 1,
        scaleY: 1,
        duration: 150,
        ease: 'Sine.Out',
      });
    } catch { /* headless */ }
    // 光暈復歸。
    try {
      if (this.glow) {
        this.scene.tweens.add({
          targets: this.glow, alpha: 0.22, scaleX: 1, scaleY: 1,
          duration: 200, ease: 'Sine.Out',
        });
      }
    } catch { /* headless */ }
  }
}
